import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ShieldCheck,
  Coins,
  BadgeCheck,
  Timer,
  Banknote,
  Award,
  CheckCircle2,
  MapPin,
  Landmark,
} from "lucide-react";
import { BRAND, IMAGES, telLink, telLinkPrimary, waLink } from "@/lib/brand";
import BrandStamp from "@/components/BrandStamp";
import usePageMeta from "@/hooks/usePageMeta";

const PhoneStrip = () => (
  <div className="bg-[#641C24] text-[#FFFDF8] border-y border-[#D4AF37]/40">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="inline-flex items-center justify-center w-9 h-9 bg-[#D4AF37] text-[#2D181A]">
          <Phone size={16} strokeWidth={2.2} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Call Now · अभी बात करें
          </div>
          <a
            href={telLinkPrimary}
            data-testid="silver-phone-strip"
            className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#FFFDF8] hover:text-[#D4AF37] transition-colors"
          >
            {BRAND.phonePrimary}
          </a>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={waLink(
            "Namaste! Mujhe apni silver bechni hai. Aaj ka best rate bataye. (Top Rate)"
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <MessageCircle size={15} /> WhatsApp
        </a>
        <a
          href={telLinkPrimary}
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-5 py-2.5 text-xs font-semibold uppercase tracking-wider"
        >
          <Phone size={15} /> Call
        </a>
      </div>
    </div>
  </div>
);

const REASONS = [
  {
    icon: BadgeCheck,
    title: "Govt-Approved Silver Valuer",
    body: "Licensed valuers accepted by banks, insurance and courts. Every gram evaluated openly, on-spot.",
  },
  {
    icon: Coins,
    title: "999-Fine Rate Benchmark",
    body: "We pay based on the live 999-fine silver rate — not a made-up 'buying rate'. Fully transparent.",
  },
  {
    icon: Timer,
    title: "Cash in 30 Minutes",
    body: "Bring silver ornaments, coins, bars or utensils — walk out with cash the same visit.",
  },
  {
    icon: Banknote,
    title: "Cash or Bank Transfer",
    body: "Instant cash upto ₹2 lakh, secure NEFT/RTGS for larger amounts. Whichever suits you.",
  },
  {
    icon: Award,
    title: "Decades of Experience",
    body: "Two branches in Gurgaon, generations of silver expertise. Serving Gurgaon, Manesar, Delhi and North India.",
  },
  {
    icon: ShieldCheck,
    title: "No Manipulation Policy",
    body: "No hidden deductions, no melting-loss games, no weight tricks. We publish everything up-front.",
  },
];

const ITEMS = [
  "Silver Ornaments (Payal, Necklaces, Bangles)",
  "Silver Coins (British India, Republic India)",
  "Silver Bars & Biscuits (999 & 995)",
  "Silver Utensils (Thali, Katori, Glass)",
  "Silver Idols & Antique Pieces",
  "Silver Jewellery (Rings, Earrings, Bracelets)",
  "Old / Broken / Scrap Silver",
  "Sterling Silver (925)",
];

const FAQ = [
  {
    q: "What is the best rate for silver in Gurgaon today?",
    a: `Silver rates change daily based on international 999-fine market prices. Call ${BRAND.phonePrimary} for today's live rate on WhatsApp or phone — quoted before you visit and locked at the branch.`,
  },
  {
    q: "Where can I sell silver in Gurgaon?",
    a: "Top Rate operates two branches in Gurgaon — Shop No. 230, Jacubpura (near Chota Gurudwara) and Shop 256, Sapphire Mall, Sector 49. We buy every form of silver — ornaments, coins, bars, utensils, sterling — at the best 999-fine market rate.",
  },
  {
    q: "How is silver purity tested?",
    a: "For ornaments and utensils, we use XRF (X-Ray Fluorescence) machine — non-destructive, 30-second accurate purity reading. For 925 sterling silver, we deduct based on the marked purity. Everything is done on your side of the counter.",
  },
  {
    q: "Do you buy old, broken or antique silver?",
    a: "Yes. Old ornaments, scrap silver, damaged coins and antique pieces are all accepted based on their silver content by weight. Antique/collector value is additional.",
  },
  {
    q: "What documents are needed to sell silver?",
    a: "A valid photo ID (Aadhaar / PAN / Voter ID / Passport / Driving License). For transactions above ₹2 lakh, PAN card is required as per Govt of India rules.",
  },
];

export default function SellSilverPage() {
  usePageMeta({
    title:
      "Sell Silver in Gurgaon — Best Rate Today | Top Rate Silver Buyer Gurugram, Delhi",
    description:
      "Get the best price for your silver today. Top Rate — govt-approved silver buyer in Gurgaon with two branches. Cash for silver ornaments, coins, bars, utensils & sterling silver. 999-fine rate. Call " +
      BRAND.phonePrimary +
      ".",
    keywords:
      "sell silver Gurgaon, silver buyer Gurugram, best rate for silver today, cash for silver, sell silver coins, sell silver ornaments, sell silver bars, sell 925 sterling silver, silver rate Gurgaon, silver buyer Delhi NCR, silver utensils buyer, antique silver buyer, scrap silver Gurgaon",
    path: "/sell-silver-gurgaon",
  });

  return (
    <div>
      <PhoneStrip />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAF6EC]">
        <img
          src={IMAGES.hero}
          alt=""
          aria-hidden="true"
          className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-25 hidden md:block"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,253,248,1) 0%, rgba(255,253,248,0.95) 45%, rgba(255,253,248,0.35) 75%, rgba(255,253,248,0) 100%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 tr-fade-up">
              <div className="inline-flex items-center gap-2 border border-[#641C24]/40 bg-[#FFFDF8] text-[#641C24] px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-bold">
                <ShieldCheck size={13} strokeWidth={2} />
                Silver · Chandi · 999 Fine
              </div>

              <h1 className="font-display text-[#2D181A] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.0] mt-6">
                Get the{" "}
                <span className="italic text-[#B8860B]">Best Price</span> for
                Your Silver Today.
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#4A3B3C] leading-relaxed max-w-2xl">
                Silver ornaments, coins, bars, utensils, sterling — Top Rate
                Gurgaon buys every form of silver at the live 999-fine market
                rate. Spot cash or bank transfer, transparent purity testing,
                govt-approved valuers.
              </p>

              {/* PRIMARY PHONE - BIG */}
              <a
                href={telLinkPrimary}
                data-testid="silver-hero-phone"
                className="mt-6 inline-flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-5 bg-[#641C24] text-[#FFFDF8] pl-5 pr-6 py-4 sm:py-5 shadow-[0_10px_30px_-10px_rgba(100,28,36,0.55)] hover:bg-[#4A151A] transition-colors w-full sm:w-auto"
              >
                <span className="inline-flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 bg-[#D4AF37] text-[#2D181A] shrink-0">
                    <Phone size={18} strokeWidth={2.4} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">
                    Call For Rate · अभी कॉल करें
                  </span>
                </span>
                <span className="font-display font-black tracking-tight text-3xl sm:text-4xl md:text-5xl leading-none text-[#FFFDF8] tabular-nums">
                  {BRAND.phonePrimary}
                </span>
              </a>

              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(
                    "Namaste! Mujhe apni silver bechni hai. Aaj ka best rate bataye. (Top Rate)"
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-[#641C24]/60 hover:bg-[#641C24] hover:text-[#FFFDF8] text-[#641C24] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider"
                >
                  <MessageCircle size={16} /> Get Silver Rate on WhatsApp
                </a>
              </div>
            </div>

            {/* Visual */}
            <div className="lg:col-span-5 flex items-center justify-center">
              <div className="relative w-full max-w-md">
                <img
                  src={IMAGES.rupees500}
                  alt="₹500 notes cash payout for silver in Gurgaon"
                  loading="eager"
                  className="w-full h-auto object-contain shadow-[0_18px_44px_-14px_rgba(45,24,26,0.4)] rotate-[-4deg]"
                />
                <img
                  src={IMAGES.rupees500}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute -bottom-6 -right-4 w-3/4 h-auto object-contain shadow-[0_16px_40px_-12px_rgba(45,24,26,0.5)] rotate-[6deg] hidden sm:block"
                />
                <div className="absolute -top-5 -left-4 bg-[#641C24] text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Silver ✦ 999
                </div>
                <div className="absolute -bottom-2 left-6 sm:left-16 bg-[#D4AF37] text-[#2D181A] text-[10px] font-bold uppercase tracking-[0.24em] px-4 py-2 shadow-lg">
                  Spot Cash
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="gold"
        tagline={
          <>Sell Silver · Gurgaon · Manesar · Delhi · Best Rate Today</>
        }
      />

      {/* WHAT WE BUY */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
          What We Buy · क्या खरीदते हैं
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
          Every form of silver —{" "}
          <span className="italic text-[#641C24]">at the best rate</span>.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
          {ITEMS.map((item) => (
            <div
              key={item}
              className="bg-white border border-[#E5DCC5] p-5 shadow-sm text-sm text-[#2D181A] leading-tight flex items-start gap-2"
            >
              <CheckCircle2 size={16} className="mt-0.5 text-[#641C24] shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={<>Sona Do Paisa Lo · Chandi ka Sahi Rate · Top Rate</>}
      />

      {/* REASONS */}
      <section className="bg-[#FAF6EC] tr-grain border-y border-[#E5DCC5]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#641C24] font-bold">
            Why Sell Silver at Top Rate
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Six reasons customers trust us for{" "}
            <span className="italic text-[#641C24]">every silver transaction</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12">
            {REASONS.map((r) => (
              <div
                key={r.title}
                className="bg-white border border-[#E5DCC5] p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-11 h-11 border border-[#D4AF37] flex items-center justify-center text-[#641C24]">
                  <r.icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-bold mt-5">
                  {r.title}
                </h3>
                <p className="text-sm text-[#4A3B3C] leading-relaxed mt-3">
                  {r.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandStamp
        variant="light"
        tagline={<>Best Silver Buyer in Gurgaon · Trusted for Decades</>}
      />

      {/* FAQ */}
      <section className="bg-[#2D181A] text-[#FFFDF8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Frequently Asked Questions
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] max-w-3xl">
            Everything you need to know about{" "}
            <span className="italic text-[#D4AF37]">selling silver</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {FAQ.map((f, i) => (
              <div key={i} className="border-l-2 border-[#D4AF37]/60 pl-6">
                <h3 className="font-display text-lg lg:text-xl font-bold text-[#FFFDF8]">
                  {f.q}
                </h3>
                <p className="text-sm text-[#FFFDF8]/80 leading-relaxed mt-3">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="bg-[#641C24] text-[#FFFDF8] p-8 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]">
                Ready to get{" "}
                <span className="italic text-[#D4AF37]">the best price</span>{" "}
                for your silver?
              </h2>
              <p className="text-[#FFFDF8]/80 mt-4 leading-relaxed">
                Call our valuer directly for today's best rate — quoted on the
                phone, locked at the branch.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href={telLinkPrimary}
                data-testid="silver-cta-call"
                className="flex items-center justify-between gap-3 bg-[#D4AF37] hover:bg-[#B8860B] text-[#2D181A] px-6 py-4"
              >
                <span className="inline-flex items-center gap-2">
                  <Phone size={16} strokeWidth={2.2} />
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    Call
                  </span>
                </span>
                <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                  {BRAND.phonePrimary}
                </span>
              </a>
              <a
                href={waLink(
                  "Namaste! Silver ka best rate bataye. (Top Rate)"
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-4"
              >
                <span className="inline-flex items-center gap-2">
                  <MessageCircle size={16} strokeWidth={2.2} />
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    WhatsApp
                  </span>
                </span>
                <span className="font-display font-black text-xl md:text-2xl tracking-tight">
                  Chat Now
                </span>
              </a>
              <Link
                to="/branches"
                className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#D4AF37] hover:text-[#FFFDF8] mt-1"
              >
                → Visit a Branch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BrandStamp
        variant="dark"
        tagline={
          <>Top Rate · Best Silver Buyer · Serving All North India</>
        }
      />
    </div>
  );
}
